/**
 * OTP form (S02) — six-digit code, calls `identity.verifyOtp`, dispatches by
 * `outcome`:
 *   existing-membership → '/'
 *   no-membership       → '/onboarding/workspace'
 *   invite-pending      → '/onboarding/workspace' (V1.x will branch)
 *
 * The `OtpField` auto-submits when all six slots fill. A separate "Verify and
 * continue" button is offered for keyboard / paste cases that don't trigger
 * the auto-submit (paste of <6 digits, etc.).
 *
 * Resend cooldown is local to this component and resets on mount; the server
 * also rate-limits per phone (Step 1's `otp_verifications.attempts` ledger).
 *
 * @see prototype/onboarding/otp.html — S02 baseline
 */
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardBody, Form, OtpField, Submit, SubmitProbe, MetaPill } from "@flint/ui";
import { identity } from "@flint/api-contract";
import type { z } from "zod";
import { trpc } from "../../../lib/trpc.ts";
import { meQueryKey } from "../../../lib/session.ts";
import { useOnboardingFlow } from "../lib/flow-state.ts";

type FormValues = z.infer<typeof identity.verifyOtpInput>;

export function OtpForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const challengeId = useOnboardingFlow((s) => s.challengeId);
  const setVerified = useOnboardingFlow((s) => s.setVerified);

  const form = useForm<FormValues>({
    resolver: zodResolver(identity.verifyOtpInput),
    defaultValues: { challengeId: challengeId ?? "", code: "" },
  });

  // Keep RHF's `challengeId` in sync if the slice loads after first render.
  useEffect(() => {
    if (challengeId) form.setValue("challengeId", challengeId);
  }, [challengeId, form]);

  const verifyOtp = trpc.identity.verifyOtp.useMutation();

  const onSubmit = form.handleSubmit(async (values) => {
    if (!values.challengeId) return; // No active challenge; user navigated here directly.
    const result = await verifyOtp.mutateAsync(values);
    setVerified(result.userId);
    // Cookie just landed; the cached `me` query (anonymous → null) is stale.
    // We must force a REFETCH (not just invalidate) before navigating: the
    // root guard's `ensureQueryData(meQueryOptions)` returns the cached value
    // when it exists regardless of staleness, and `invalidateQueries` only
    // triggers an active-subscriber refetch — there are none on this page.
    // Without an explicit `refetchQueries`, the guard sees the stale `null`
    // and bounces the freshly-signed-in user back to /sign-in.
    await queryClient.refetchQueries({ queryKey: meQueryKey });
    if (result.outcome === "existing-membership") {
      // Members land on the module launcher (ADR-0049), not straight into
      // a module — it's the app's front door.
      void navigate({ to: "/launch" });
    } else {
      void navigate({ to: "/onboarding/workspace" });
    }
  });

  // Auto-submit when six digits are entered. Watching the field is local to
  // this form (the wrapper stays generic) and uses `formState.isSubmitting`
  // to avoid double-firing while a submit is in flight.
  //
  // `lastAttemptedCode` guards against a retry loop: if a verify call fails
  // (wrong code, expired, attempts exceeded), the input still holds 6 digits
  // and the effect's other inputs (`isPending`, `isSubmitting`) settle back
  // to false — without this guard the effect would re-fire forever, hammering
  // the server. We only auto-submit when the user types a *different* code
  // than the last one we attempted.
  const code = form.watch("code");
  const lastAttemptedCode = useRef<string | null>(null);
  useEffect(() => {
    if (
      code?.length === 6 &&
      code !== lastAttemptedCode.current &&
      !form.formState.isSubmitting &&
      !verifyOtp.isPending &&
      challengeId
    ) {
      lastAttemptedCode.current = code;
      void onSubmit();
    }
  }, [code, form.formState.isSubmitting, verifyOtp.isPending, challengeId, onSubmit]);

  return (
    <Form {...form}>
      <SubmitProbe>
        <form onSubmit={onSubmit} noValidate>
          <Card>
            <CardBody className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-1.5">
                <MetaPill>Single-use code</MetaPill>
                <MetaPill>5-minute expiry</MetaPill>
              </div>

              <OtpField
                name="code"
                length={6}
                helper="Paste works. The field advances automatically as each digit arrives."
                autoSubmitOnComplete
              />

              <Submit pendingLabel="Verifying" disabled={verifyOtp.isPending}>
                Verify and continue
              </Submit>

              <ResendRow />

              {verifyOtp.error ? (
                <p role="alert" className="text-xs text-bad">
                  {verifyOtp.error.message}
                </p>
              ) : null}
            </CardBody>
          </Card>
        </form>
      </SubmitProbe>
    </Form>
  );
}

function ResendRow() {
  const [secondsLeft, setSecondsLeft] = useState(30);
  const requestOtp = trpc.identity.requestOtp.useMutation();
  const phone = useOnboardingFlow((s) => s.phone);
  const setChallenge = useOnboardingFlow((s) => s.setChallenge);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const onResend = async () => {
    if (!phone || secondsLeft > 0) return;
    const result = await requestOtp.mutateAsync({ phone, channel: "sms" });
    setChallenge(result.challengeId);
    setSecondsLeft(30);
  };

  return (
    <div className="flex items-center justify-between text-xs text-muted">
      <span>Didn't get the code?</span>
      <button
        type="button"
        onClick={onResend}
        disabled={secondsLeft > 0 || requestOtp.isPending}
        className="font-medium text-sky-600 disabled:text-muted disabled:cursor-not-allowed hover:underline"
      >
        {secondsLeft > 0 ? (
          <>
            Resend in <span className="font-mono tabular-nums">{secondsLeft}</span>s
          </>
        ) : (
          "Resend code"
        )}
      </button>
    </div>
  );
}
